import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchDepartment, fetchDepartmentTasks, createAction, fetchActionsByDepartment } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function DepartmentActionAddPage() {
  const { businessDomainId, businessUnitId, departmentId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}`;

  const [department, setDepartment] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [existingActions, setExistingActions] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [form, setForm] = useState({ actionName: "", actionDescription: "" });

  useEffect(() => {
    (async () => {
      try {
        const [d, taskList, actions] = await Promise.all([
          fetchDepartment(departmentId),
          fetchDepartmentTasks(departmentId),
          fetchActionsByDepartment(departmentId),
        ]);
        setDepartment(d);
        setTasks(taskList || []);
        setExistingActions(Array.isArray(actions) ? actions : []);
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleTaskChange = (e) => {
    const task = tasks.find((tk) => tk.taskId === e.target.value);
    setSelectedTask(task || null);
    if (task) {
      // 旧アプリと同様にタスク名でアクション名・説明を自動入力
      const sep = navigator.language === "ja" ? "。" : ".";
      setForm({ actionName: task.taskName, actionDescription: task.taskName + sep });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTask) {
      notify.error(t("department.action.task.required"));
      return;
    }
    const isDuplicate = existingActions.some(
      (a) => a.actionName === form.actionName.trim()
    );
    if (isDuplicate) {
      notify.error(t("department.action.error.duplicateName"));
      return;
    }
    try {
      await createAction({
        companyId: COMPANY_ID,
        taskId: selectedTask.taskId,
        taskName: selectedTask.taskName,
        actionName: form.actionName,
        actionDescription: form.actionDescription || null,
        departmentId: department.departmentId,
        departmentName: department.departmentName,
      });
      notify.success(t("message.create.success"));
      navigate(`${basePath}/actions`);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("department.action.add.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>{t("department.action.task")}</InputLabel>
              <Select
                value={selectedTask?.taskId || ""}
                onChange={handleTaskChange}
                label={t("department.action.task")}
              >
                {tasks.map((task) => (
                  <MenuItem key={task.taskId} value={task.taskId}>{task.taskName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              required
              label={t("department.action.name")}
              name="actionName"
              value={form.actionName}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t("common.description")}
              name="actionDescription"
              value={form.actionDescription}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.add")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}/actions`)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
